import Vue from 'vue'
import VueRouter from 'vue-router'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

Vue.use(VueRouter)

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (location) {
  return originalPush.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
      return Promise.reject(err)
    }
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace (location) {
  return originalReplace.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
      return Promise.reject(err)
    }
  })
}

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/login.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      title: 'ログイン'
    }
  },
  {
    path: '/password_recovery',
    name: 'password_recovery',
    component: () => import('@/pages/password_recovery.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      title: 'パスワード再設定'
    }
  },
  {
    path: '/email_action',
    name: 'email_action',
    component: () => import('@/pages/email_action.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false
    }
  },
  {
    path: '/password_updated',
    name: 'password_updated',
    component: () => import('@/pages/password_updated.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      title: 'パスワード変更完了'
    }
  },
  {
    path: '/create_account',
    name: 'create_account',
    component: () => import('@/pages/create_account.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: false,
      title: '会員登録'
    }
  },
  {
    path: '/fill_profile',
    name: 'fill_profile',
    component: () => import('@/pages/fill_profile.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: true,
      title: '本人情報登録'
    }
  },
  {
    path: '/account_created',
    name: 'account_created',
    component: () => import('@/pages/account_created.vue'),
    meta: {
      layout: 'auth',
      requiresAuth: true,
      title: '会員登録完了'
    }
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/pages/account.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'アカウント設定'
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/settings.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'プロファイル設定'
    }
  },
  {
    path: '/templates',
    name: 'templates',
    component: () => import('@/pages/templates.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'テンプレートを選択してください'
    }
  },
  {
    path: '/templates/:id',
    name: 'template',
    component: () => import('@/pages/template.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'テンプレート'
    }
  },
  {
    path: '/templates/:id/preview',
    name: 'template_preview',
    component: () => import('@/pages/preview.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'プレビュー'
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/pages/projects.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'プロジェクト一覧'
    }
  },
  {
    path: '/projects/:id/preview',
    name: 'project_preview',
    component: () => import('@/pages/preview.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'プレビュー'
    }
  },
  {
    path: '/designer/:id',
    name: 'designer',
    component: () => import('@/pages/designer.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'デザイナー'
    }
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/pages/users.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'ユーザー一覧'
    }
  },
  {
    path: '/users/:id',
    name: 'user',
    component: () => import('@/pages/user.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'ユーザー'
    }
  },
  {
    path: '/users/:uid/projects/:id/preview',
    name: 'user_project_preview',
    component: () => import('@/pages/preview.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'プレビュー'
    }
  },
  {
    path: '/isbn',
    name: 'isbn',
    component: () => import('@/pages/isbn.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      title: 'ISBN'
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  const currentUser = await getCurrentUser()

  if (to.meta.requiresAuth && !currentUser) {
    next({
      path: '/login',
      query: { redirect: to.fullPath === '/' ? '/projects' : to.fullPath }
    })
  } else if (to.path === '/') {
    next({ path: '/projects' })
  } else {
    next()
  }
})

router.afterEach((to, from) => {
  Vue.nextTick(() => {
    if (to.name === 'email_action') {
      switch (to.query.mode) {
        case 'resetPassword':
          document.title = '新しいパスワードを作成 | Booko'
          break
        case 'verifyEmail':
          document.title = 'メールアドレスの承認 | Booko'
          break
        case 'recoverEmail':
          document.title = 'メールの回復 | Booko'
          break
      }
    } else {
      document.title = to.meta.title ? to.meta.title + ' | Booko' : 'Booko'
    }
  })
})

function getCurrentUser () {
  const auth = getAuth()

  return new Promise((resolve) => {
    if (auth.currentUser) return resolve(auth.currentUser)
    onAuthStateChanged(getAuth(), (user) => resolve(user))
  })
}

export default router
